import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, Loader2, XCircle, AlertCircle, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id'); // Stripe
  const mpReference = searchParams.get('mp_reference'); // Mercado Pago
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [provider, setProvider] = useState(null);

  useEffect(() => {
    if (token && (sessionId || mpReference)) {
      if (sessionId) {
        setProvider('stripe');
        checkStripeStatus();
      } else if (mpReference) {
        setProvider('mercadopago');
        checkMercadoPagoStatus();
      }
    }
  }, [sessionId, mpReference, token]);

  const checkStripeStatus = async () => {
    const maxAttempts = 5;
    
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(response.data);
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else if (attempts < maxAttempts) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          checkStripeStatus();
        }, 2000);
      } else {
        setStatus('timeout');
      }
    } catch (error) {
      console.error('Error checking Stripe payment:', error);
      setStatus('error');
    }
  };

  const checkMercadoPagoStatus = async () => {
    const maxAttempts = 5;
    
    try {
      const response = await axios.get(`${API}/payments/mercadopago/status/${mpReference}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid' || response.data.status === 'success') {
        setStatus('success');
        setPaymentData(response.data);
      } else if (response.data.mercadopago_status === 'approved') {
        setStatus('success');
        setPaymentData(response.data);
      } else if (attempts < maxAttempts) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          checkMercadoPagoStatus();
        }, 2000);
      } else {
        // Si después de varios intentos no confirma, mostrar como pendiente
        setStatus('pending');
      }
    } catch (error) {
      console.error('Error checking MercadoPago payment:', error);
      if (attempts < maxAttempts) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          checkMercadoPagoStatus();
        }, 2000);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen dark" style={{ background: 'linear-gradient(135deg, #080c2d 0%, #1a1a3e 100%)' }} data-testid="payment-success-page">
      <div className="container mx-auto px-6 py-16 flex items-center justify-center min-h-screen">
        <Card className="card-viubux-dark border-purple-500/20 max-w-md w-full" data-testid="payment-status-card">
          <CardHeader className="text-center">
            {status === 'checking' && (
              <>
                <div className="flex justify-center mb-4">
                  <Loader2 className="w-16 h-16 animate-spin" style={{ color: '#7012b2' }} />
                </div>
                <CardTitle className="gotham-bold text-2xl text-white">Verificando Pago</CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Por favor espera mientras verificamos tu transacción con {provider === 'mercadopago' ? 'Mercado Pago' : 'Stripe'}...
                </CardDescription>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="flex justify-center mb-4">
                  <CheckCircle className="w-16 h-16 text-green-500" />
                </div>
                <CardTitle className="gotham-bold text-2xl text-white">¡Pago Exitoso!</CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Tu membresía ha sido activada correctamente
                </CardDescription>
              </>
            )}

            {status === 'pending' && (
              <>
                <div className="flex justify-center mb-4">
                  <AlertCircle className="w-16 h-16 text-yellow-500" />
                </div>
                <CardTitle className="gotham-bold text-2xl text-white">Pago Pendiente</CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Tu pago está siendo procesado. Recibirás una confirmación por correo cuando se complete.
                </CardDescription>
              </>
            )}

            {(status === 'error' || status === 'expired' || status === 'timeout') && (
              <>
                <div className="flex justify-center mb-4">
                  <XCircle className="w-16 h-16 text-red-500" />
                </div>
                <CardTitle className="gotham-bold text-2xl text-white">Error en el Pago</CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  {status === 'expired' ? 'La sesión de pago ha expirado' : 'Hubo un problema al procesar tu pago'}
                </CardDescription>
              </>
            )}
          </CardHeader>

          <CardContent className="space-y-4">
            {status === 'success' && paymentData?.access_code && (
              <div className="bg-black/30 rounded-lg p-6 text-center" data-testid="access-code-display">
                <p className="verdana-regular text-white/70 mb-2 text-sm">Tu código de acceso:</p>
                <code className="gotham-black text-3xl text-green-400 block mb-4">
                  {paymentData.access_code}
                </code>
                <p className="verdana-regular text-white/60 text-xs">
                  Este código también ha sido enviado a tu correo electrónico
                </p>
              </div>
            )}

            {status === 'pending' && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                <p className="text-yellow-400 text-sm">
                  Los pagos con algunos métodos pueden tardar hasta 72 horas en confirmarse.
                </p>
              </div>
            )}

            <Button
              data-testid="go-to-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              className="w-full btn-viubux flex items-center justify-center gap-2"
            >
              Ir al Dashboard
              <ArrowRight className="w-4 h-4" />
            </Button>

            {(status === 'error' || status === 'expired' || status === 'timeout') && (
              <Button
                data-testid="retry-payment-btn"
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="w-full border-purple-500/50 text-white hover:bg-purple-500/20"
              >
                Intentar de Nuevo
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;
